import { useState, useCallback, useEffect, useRef } from 'react';
import { X, RotateCcw, Upload, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FloatingControls from './FloatingControls';
import RecordingSetupPanel, { COUNTDOWN_OPTIONS, DEFAULT_COUNTDOWN_SECONDS } from './RecordingSetupPanel';
import RecordingCountdown from './RecordingCountdown';
import PreviewAndCrop from './PreviewAndCrop';
import { useRecording, RecordingMode } from '../hooks/useRecording';
import { DEFAULT_CAMERA_BUBBLE, isCameraBubble, type CameraBubble } from '../lib/capture/compositor';
import {
  isCameraPreviewWindowSupported,
  isEmbedded,
  onHostCameraPreview,
  openCameraPreviewWindow,
  requestHostCameraPreview,
  sendHostCameraPreviewStatus,
  type CameraPreviewWindow,
  type PreviewCommand,
} from '../lib/capture/cameraPreviewWindow';
import { useVideos } from '../hooks/useVideos';
import { shouldRelaxDisplayCapture } from '../utils/displayCaptureRelaxed';
import StorageExceededDialog from './StorageExceededDialog';
import RichTextField from './RichTextField';
import { TITLE_MAX_LENGTH, DESCRIPTION_MAX_CHARS } from './EditVideoDetailsDialog';
import { richTextToPlain } from '../lib/richText';
import { useStorage, StorageInfo } from '../hooks/useStorage';
import { toast } from '../lib/toast';
import {
  notifyAcvRecordingState,
  ACV_STOP_RECORDING_MESSAGE,
  type AcvRecordingState,
} from '../utils/notifyAcvRecordingState';

interface ScreenRecorderProps {
  isOpen: boolean;
  onClose?: () => void;
  initialRecordingMode?: RecordingMode;
  /** true while the recorder covers the page (setup, area picker, preview); false while recording. */
  onBlockingChange?: (blocking: boolean) => void;
}

// Internal component that contains all the hooks
const ScreenRecorderContent: React.FC<Omit<ScreenRecorderProps, 'isOpen'>> = ({ onClose, initialRecordingMode, onBlockingChange }) => {
  const { t } = useTranslation();
  const [showSetupPanel, setShowSetupPanel] = useState(true);
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  // Seconds left before recording starts (null = no countdown running).
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<{ timer: number; resolve: (go: boolean) => void } | null>(null);
  const unmountedRef = useRef(false);

  /** Ends a running countdown: `go` starts the recording, false cancels it. */
  const finishCountdown = useCallback((go: boolean) => {
    const running = countdownRef.current;
    if (!running) return;
    countdownRef.current = null;
    window.clearInterval(running.timer);
    setCountdown(null);
    // Let the overlay disappear before the first frame is recorded.
    if (go) window.setTimeout(() => running.resolve(!unmountedRef.current), 150);
    else running.resolve(false);
  }, []);

  /** Runs after the screen is picked and before the recorder starts (see useRecording). */
  const countdownBeforeStart = useCallback((seconds: number) => (signal: AbortSignal): Promise<boolean> => {
    if (seconds <= 0 || signal.aborted) return Promise.resolve(!signal.aborted);
    return new Promise<boolean>((resolve) => {
      let left = seconds;
      setCountdown(left);
      const timer = window.setInterval(() => {
        left -= 1;
        if (left <= 0) finishCountdown(true);
        else setCountdown(left);
      }, 1000);
      countdownRef.current = { timer, resolve };
      // The user stopped sharing: cancel now.
      signal.addEventListener('abort', () => finishCountdown(false), { once: true });
    });
  }, [finishCountdown]);

  // Closing the recorder mid-countdown cancels it (and releases the picked screen).
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      finishCountdown(false);
    };
  }, [finishCountdown]);
  const [showPreview, setShowPreview] = useState(false);
  const [showAreaSelector, setShowAreaSelector] = useState(false);
  const [isSelectingScreen, setIsSelectingScreen] = useState(false);
  const [selectedScreenStream, setSelectedScreenStream] = useState<MediaStream | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [storageExceeded, setStorageExceeded] = useState(false);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [recordingOptions, setRecordingOptions] = useState(() => {
    // Restore saved preferences from localStorage, including selectedArea.
    const defaults = {
      recordScreen: true,
      recordAudio: true,
      recordCamera: false,
      cameraBubble: DEFAULT_CAMERA_BUBBLE as CameraBubble,
      countdownSeconds: DEFAULT_COUNTDOWN_SECONDS as number,
      recordingMode: (initialRecordingMode || 'fullScreen') as RecordingMode,
      selectedArea: undefined as { x: number; y: number; width: number; height: number } | undefined,
    };
    try {
      const saved = localStorage.getItem('airecording-setup-prefs');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate selectedArea shape before restoring — guards against malformed data.
        const area = parsed.selectedArea;
        const validArea =
          area &&
          typeof area.x === 'number' &&
          typeof area.y === 'number' &&
          typeof area.width === 'number' &&
          typeof area.height === 'number' &&
          area.width > 0 &&
          area.height > 0
            ? area
            : undefined;
        return {
          ...defaults,
          ...parsed,
          // initialRecordingMode (from URL/prop) always takes priority over saved pref.
          recordingMode: (initialRecordingMode || parsed.recordingMode || 'fullScreen') as RecordingMode,
          selectedArea: validArea,
          recordCamera: parsed.recordCamera === true,
          cameraBubble: isCameraBubble(parsed.cameraBubble) ? parsed.cameraBubble : DEFAULT_CAMERA_BUBBLE,
          countdownSeconds: (COUNTDOWN_OPTIONS as readonly number[]).includes(parsed.countdownSeconds)
            ? parsed.countdownSeconds
            : DEFAULT_COUNTDOWN_SECONDS,
        };
      }
    } catch { /* ignore malformed localStorage */ }
    return defaults;
  });
  
  const videoRef = useRef<HTMLVideoElement>(null);

  // Persist all setup preferences including selectedArea whenever they change.
  useEffect(() => {
    try { localStorage.setItem('airecording-setup-prefs', JSON.stringify(recordingOptions)); } catch { /* quota full */ }
  }, [recordingOptions]);

  const {
    isRecording,
    isPaused,
    recordedBlob,
    recordingTime,
    recordingTimeSeconds,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
    hasCamera,
    isCameraOn,
    toggleCamera,
    cameraStream,
    captureSurface,
    setBubbleSuppressed,
    hasMic,
    isMicOn,
    toggleAudio,
  } = useRecording();

  // ── Live camera preview while recording ──────────────────────────────────
  // An always-on-top Picture-in-Picture window (Chrome/Edge) so presenters see themselves in
  // other windows; other browsers get a small preview in the floating controls. Inside ACV the
  // window is opened by ACV (iframes can't open one) — `embedded` switches to that path.
  const pipSupported = isCameraPreviewWindowSupported();
  const embedded = isEmbedded();
  const previewRef = useRef<CameraPreviewWindow | null>(null);
  const openingPreviewRef = useRef(false);
  const autoPreviewTriedRef = useRef(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const closePreview = useCallback(() => {
    if (embedded) requestHostCameraPreview('close');
    previewRef.current?.close();
    previewRef.current = null;
    setPreviewOpen(false);
  }, [embedded]);

  // Pause/Resume/Stop clicked in the preview window. Assigned further down, once the handlers
  // exist; read through a ref so the subscriptions don't need to change.
  const previewCommandRef = useRef<(command: PreviewCommand) => void>(() => {});

  // ACV reports when its window opened, failed or was closed by the user, and relays its buttons.
  useEffect(
    () =>
      embedded
        ? onHostCameraPreview({
            onState: (open, cameraError) => {
              setPreviewOpen(open);
              if (cameraError) {
                toast.warning(t('camera_preview_blocked', 'Camera preview needs camera access for ACV. Allow it in the browser and try again.'));
              }
            },
            onCommand: (command) => previewCommandRef.current(command),
          })
        : undefined,
    [embedded, t],
  );

  // Called synchronously from clicks (and right after start): the message must go out while the
  // click still counts, so no await before it.
  const openPreview = useCallback(async () => {
    if (embedded) {
      if (cameraStream) requestHostCameraPreview('open', recordingOptions.cameraBubble?.shape ?? 'circle');
      return;
    }
    if (!cameraStream || previewRef.current || openingPreviewRef.current) return;
    openingPreviewRef.current = true;
    try {
      previewRef.current = await openCameraPreviewWindow(
        cameraStream,
        (command) => previewCommandRef.current(command),
        () => { previewRef.current = null; setPreviewOpen(false); }, // closed by the user
      );
      setPreviewOpen(true);
    } catch {
      // Usually "no recent click": the floating controls offer a button instead.
    } finally {
      openingPreviewRef.current = false;
    }
  }, [embedded, cameraStream, recordingOptions.cameraBubble?.shape]);

  // Try once when a camera recording starts; close when it ends.
  useEffect(() => {
    if (!pipSupported) return;
    if (isRecording && hasCamera && cameraStream && !autoPreviewTriedRef.current) {
      autoPreviewTriedRef.current = true;
      void openPreview();
    }
    if (!isRecording) {
      autoPreviewTriedRef.current = false;
      if (previewRef.current || previewOpen) closePreview();
    }
  }, [pipSupported, isRecording, hasCamera, cameraStream, previewOpen, openPreview, closePreview]);

  // A whole-screen capture (full screen, or an area of a screen) records the preview window
  // itself: hide the drawn bubble meanwhile so the camera isn't in the video twice. The video
  // then shows exactly what's on screen — in area mode, the window counts only inside the area.
  const previewIsCaptured = previewOpen && captureSurface === 'monitor';
  useEffect(() => {
    setBubbleSuppressed(previewIsCaptured);
  }, [previewIsCaptured, setBubbleSuppressed]);
  const areaHintShownRef = useRef(false);
  useEffect(() => {
    if (!isRecording) { areaHintShownRef.current = false; return; }
    if (previewIsCaptured && recordingOptions.recordingMode === 'selectArea' && !areaHintShownRef.current) {
      areaHintShownRef.current = true;
      toast.info(t('camera_preview_area_hint', 'Your camera is recorded where the preview window is: keep it inside the selected area.'));
    }
  }, [isRecording, previewIsCaptured, recordingOptions.recordingMode, t]);

  useEffect(() => () => {
    previewRef.current?.close();
    if (embedded) requestHostCameraPreview('close');
  }, [embedded]);

  // Hiding the camera also closes its preview; showing it again reopens it (this click allows it).
  const handleToggleCamera = useCallback(() => {
    const turningOn = !isCameraOn;
    toggleCamera();
    if (!pipSupported) return;
    if (turningOn) void openPreview();
    else closePreview();
  }, [isCameraOn, toggleCamera, pipSupported, openPreview, closePreview]);

  const { uploadVideo } = useVideos();
  const { getStorageInfo } = useStorage();

  // Function to select screen for area selection and show preview for cropping
  const selectScreenForAreaSelection = useCallback(async () => {
    console.log('[ScreenRecorder] Selecting screen for area selection');
    setIsSelectingScreen(true);
    
    try {
      const relaxed = shouldRelaxDisplayCapture();
      const strictVideo: Record<string, unknown> = {
        frameRate: { ideal: 30 },
        displaySurface: 'monitor',
        width: { ideal: window.screen.width },
        height: { ideal: window.screen.height },
      };
      const relaxedVideo: Record<string, unknown> = {
        frameRate: { ideal: 30 },
      };

      // Chrome normally switches to the tab/window just picked, hiding the crop screen.
      // CaptureController (Chrome/Edge) keeps focus here so the area can be drawn first.
      const CaptureControllerCtor = (window as unknown as {
        CaptureController?: new () => { setFocusBehavior?: (b: string) => void };
      }).CaptureController;
      const makeController = () => (CaptureControllerCtor ? new CaptureControllerCtor() : undefined);
      const getDisplayMedia = (navigator.mediaDevices as unknown as {
        getDisplayMedia: (c?: unknown) => Promise<MediaStream>;
      }).getDisplayMedia.bind(navigator.mediaDevices);
      const pick = async (video: Record<string, unknown>) => {
        const controller = makeController();
        const stream = await getDisplayMedia({
          video,
          audio: true,
          // Offer the current tab in the picker (Chrome may hide it by default).
          selfBrowserSurface: 'include',
          ...(controller ? { controller } : {}),
        });
        // Must run right after the promise resolves; only valid for tabs/windows.
        const surface = stream.getVideoTracks()[0]?.getSettings?.().displaySurface;
        if (controller?.setFocusBehavior && (surface === 'browser' || surface === 'window')) {
          try { controller.setFocusBehavior('no-focus-change'); } catch { /* too late or unsupported */ }
        }
        return stream;
      };

      let screenStream: MediaStream;
      try {
        screenStream = await pick(relaxed ? relaxedVideo : strictVideo);
      } catch (firstErr) {
        if (!relaxed) {
          console.warn('[ScreenRecorder] getDisplayMedia strict failed, retrying relaxed:', firstErr);
          screenStream = await pick(relaxedVideo);
        } else {
          throw firstErr;
        }
      }
      console.log('[ScreenRecorder] Screen selected successfully');
      
      // Store the selected screen stream
      setSelectedScreenStream(screenStream);
      
      // Now show the preview and crop UI with the selected screen
      console.log('[ScreenRecorder] Showing preview and crop UI for selected screen');
      setShowAreaSelector(true); // We'll reuse this state for showing the PreviewAndCrop component
      setShowSetupPanel(false);
      setIsSelectingScreen(false);
      
      // Add track ended listener to clean up if user cancels
      screenStream.getVideoTracks().forEach((track: MediaStreamTrack) => {
        track.onended = () => {
          console.log('[ScreenRecorder] Screen sharing ended by user');
          setSelectedScreenStream(null);
          setShowAreaSelector(false);
          setShowSetupPanel(true);
        };
      });
    } catch (err) {
      console.error('[ScreenRecorder] Error selecting screen:', err);
      setIsSelectingScreen(false);
      setShowSetupPanel(true);
    }
  }, []);
  
  const handleStartRecording = useCallback(() => {
    console.log('[ScreenRecorder] Starting recording with mode:', recordingOptions.recordingMode);
    
    if (recordingOptions.recordingMode === 'selectArea') {
      // For area selection, first select the screen, then show the selector overlay
      selectScreenForAreaSelection();
    } else {
      // For full screen, start recording directly
      console.log('[ScreenRecorder] Starting full screen recording');
      startRecording(recordingOptions, undefined, countdownBeforeStart(recordingOptions.countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS))
        .then((started) => {
          if (!started) return; // picker cancelled or failed: stay on the setup panel
          console.log('[ScreenRecorder] Full screen recording started successfully');
          setIsRecordingActive(true);
          setShowSetupPanel(false);
        })
        .catch(err => {
          console.error('[ScreenRecorder] Failed to start full screen recording:', err);
        });
    }
  }, [recordingOptions, startRecording, countdownBeforeStart]);

  const handleStopRecording = useCallback(async () => {
    if (!isRecording) return; // Prevent multiple stops
    
    try {
      console.log('Stopping recording...');
      // The preview opens from the effect below, which also covers stops that don't come
      // from this button (the browser's "Stop sharing", closing the shared window).
      await stopRecording();
    } catch (err) {
      console.error('Failed to stop recording:', err);
      setIsRecordingActive(false);
      if (!recordedBlob) {
        toast.error(t('recording_stop_failed', 'The recording could not be saved.'));
        setShowSetupPanel(true);
      }
    }
  }, [stopRecording, isRecording, recordedBlob, t]);

  // Recording finished (any cause): open the preview, or go back to setup if it's empty.
  useEffect(() => {
    if (isRecording || !isRecordingActive || !recordedBlob) return;
    setIsRecordingActive(false);
    if (recordedBlob.size > 0) {
      setVideoUrl(URL.createObjectURL(recordedBlob));
      setShowPreview(true);
    } else {
      console.warn('No valid video blob available');
      toast.error(t('recording_empty', 'Nothing was recorded. Please try again.'));
      setShowSetupPanel(true);
    }
  }, [isRecording, isRecordingActive, recordedBlob, t]);

  const handlePauseRecording = useCallback(() => {
    pauseRecording();
  }, [pauseRecording]);

  const handleResumeRecording = useCallback(() => {
    resumeRecording();
  }, [resumeRecording]);

  // Preview window: its buttons act like the floating controls; it shows the same time.
  previewCommandRef.current = (command) => {
    if (command === 'pause') handlePauseRecording();
    else if (command === 'resume') handleResumeRecording();
    else void handleStopRecording();
  };
  useEffect(() => {
    if (!previewOpen) return;
    const status = { time: recordingTime, paused: isPaused };
    if (embedded) sendHostCameraPreviewStatus(status);
    else previewRef.current?.setStatus(status);
  }, [previewOpen, recordingTime, isPaused, embedded]);

  const handleDeleteRecording = useCallback(() => {
    resetRecording();
    setIsRecordingActive(false);
    setShowSetupPanel(true);
    if (onClose) onClose();
  }, [resetRecording, onClose]);

  const handleSave = useCallback(async () => {
    if (!recordedBlob || !title.trim()) return;
    if (richTextToPlain(description).length > DESCRIPTION_MAX_CHARS) {
      toast.error(t('video_details.description_too_long', 'The description is too long.'));
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadVideo(recordedBlob, title.trim(), description || undefined, recordingTimeSeconds);
      if (result.status === 'storage_exceeded') {
        setStorageInfo(await getStorageInfo());
        setStorageExceeded(true);
        return;
      }
      if (result.status === 'success') {
        toast.success(t('video_editor_save.upload_ok'));
        onClose?.();
      } else {
        toast.error(t('video_editor_save.upload_fail'));
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(t('video_editor_save.upload_fail'));
    } finally {
      setIsUploading(false);
    }
  }, [recordedBlob, title, description, uploadVideo, onClose, t]);
  
  const handleDownload = useCallback(() => {
    if (!recordedBlob) return;
    
    const url = URL.createObjectURL(recordedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'recording'}-${Date.now()}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [recordedBlob, title]);
  
  const handleReset = useCallback(() => {
    resetRecording();
    setShowPreview(false);
    setTitle('');
    setDescription('');
    setShowSetupPanel(true);
  }, [resetRecording]);

  // Free the preview URL when it's replaced (re-record) or the recorder closes.
  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  // Closing the recorder while the area picker is open (Esc, ✕) must release the screen that
  // was picked but never recorded, or the browser keeps showing "sharing". An active recording
  // is released by useRecording's own unmount cleanup.
  const selectedScreenStreamRef = useRef<MediaStream | null>(null);
  selectedScreenStreamRef.current = selectedScreenStream;
  useEffect(() => () => {
    selectedScreenStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  // While recording, the page stays usable (the user may be recording this very tab), so
  // the recorder drops its backdrop and only the floating controls take clicks.
  const isLive = (isRecording || isRecordingActive) && !showPreview;
  // The save screen holds a recording that isn't saved yet: closing it asks first.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const confirmDiscardRef = useRef(false);
  confirmDiscardRef.current = confirmDiscard;
  const reviewing = showPreview && !!recordedBlob;
  const requestClose = useCallback(() => {
    if (isUploading) return; // the upload finishes (or fails) first
    if (reviewing) setConfirmDiscard(true);
    else onClose?.();
  }, [isUploading, reviewing, onClose]);

  // Let the host know when the recorder blocks the page (setup, area picker, preview)
  // and when it doesn't (recording), so ACV only veils its navbar when needed.
  useEffect(() => {
    onBlockingChange?.(!isLive);
  }, [isLive, onBlockingChange]);

  // Tell ACV when leaving the page would lose a recording, so it asks before switching
  // pages. Saving or closing the recorder unmounts it, which reports 'idle'.
  const leaveState: AcvRecordingState = isLive ? 'recording' : showPreview && recordedBlob ? 'unsaved' : 'idle';
  useEffect(() => {
    notifyAcvRecordingState(leaveState);
  }, [leaveState]);
  useEffect(() => () => notifyAcvRecordingState('idle'), []);

  // "Stop recording" from ACV's leave dialog: same as the floating Stop button (opens the preview).
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== window.parent || e.data?.type !== ACV_STOP_RECORDING_MESSAGE) return;
      void handleStopRecording();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleStopRecording]);

  // Esc closes the recorder before recording starts. While recording, Esc belongs to the
  // page the user is working in; recording is stopped with the floating controls.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (countdownRef.current) {
        finishCountdown(false);
        return;
      }
      // Esc on the question keeps the recording.
      if (confirmDiscardRef.current) {
        setConfirmDiscard(false);
        return;
      }
      // Another dialog on top (e.g. inserting a link in the description) handles its own Esc.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (!isLive) requestClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLive, requestClose, finishCountdown]);

  // Format recording time as MM:SS
  const formatRecordingTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle area selection from the PreviewAndCrop component
  const handleAreaSelected = useCallback((area: { x: number; y: number; width: number; height: number }) => {
    console.log('[ScreenRecorder] Area selected from preview:', area);
    
    if (!area || typeof area.x !== 'number' || typeof area.width !== 'number') {
      console.error('[ScreenRecorder] Invalid area selection received:', area);
      setShowSetupPanel(true);
      return;
    }
    
    try {
      // Check if we have a selected screen stream
      if (!selectedScreenStream) {
        console.error('[ScreenRecorder] No selected screen stream available');
        setShowSetupPanel(true);
        return;
      }
      
      // The area coordinates are already scaled to the original stream dimensions by PreviewAndCrop
      // Update recording options with the selected area
      const updatedOptions = {
        ...recordingOptions,
        recordingMode: 'selectArea' as const,
        selectedArea: area // Already a deep copy from PreviewAndCrop
      };
      
      console.log('[ScreenRecorder] Updated recording options with scaled coordinates:', JSON.stringify(updatedOptions));
      
      // Update state and hide the preview
      setRecordingOptions(updatedOptions);
      setShowAreaSelector(false);
      
      // Start recording with the selected area and the already selected screen stream
      console.log('[ScreenRecorder] Starting recording with selected area on selected screen...');
      
      // From here the recorder owns the capture: "Stop sharing" must stop and keep the
      // recording (handled in useRecording), not reset to the setup panel.
      selectedScreenStream.getVideoTracks().forEach((track) => { track.onended = null; });

      try {
        const seconds = updatedOptions.countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS;
        // With a countdown, the time to switch windows comes before the recording starts.
        if (seconds > 0) {
          toast.info(t('area_recording_countdown', {
            count: seconds,
            defaultValue: 'Recording starts in {{count}} s. Switch to the window you are recording.',
          }));
        }
        // Start recording with the selected area and screen
        startRecording(updatedOptions, selectedScreenStream, countdownBeforeStart(seconds))
          .then((started) => {
            setSelectedScreenStream(null);
            if (!started) {
              setShowSetupPanel(true);
              return;
            }
            setIsRecordingActive(true);
            if (seconds <= 0) {
              toast.info(t('area_recording_started', 'Recording started. Switch to the window you are recording.'));
            }
          })
          .catch(err => {
            console.error('[ScreenRecorder] Failed to start recording with selected area:', err);
            setShowSetupPanel(true);
          });
      } catch (err) {
        console.error('[ScreenRecorder] Error starting recording:', err);
        setShowSetupPanel(true);
      }
    } catch (err) {
      console.error('[ScreenRecorder] Error in handleAreaSelected:', err);
      setShowSetupPanel(true);
    }
  }, [recordingOptions, startRecording, selectedScreenStream, t, countdownBeforeStart]);

  const handleAreaSelectionCancel = useCallback(() => {
    console.log('[ScreenRecorder] Area selection cancelled');
    
    // Clean up the selected screen stream if it exists
    if (selectedScreenStream) {
      console.log('[ScreenRecorder] Cleaning up selected screen stream');
      selectedScreenStream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
      });
      setSelectedScreenStream(null);
    }
    
    setShowAreaSelector(false);
    setShowSetupPanel(true);
  }, [selectedScreenStream]);

  // Use a single return statement with conditional rendering to avoid hooks errors
  return (
    <>
      {countdown !== null && (
        <RecordingCountdown seconds={countdown} onCancel={() => finishCountdown(false)} />
      )}

      {/* Screen Selection Loading Indicator */}
      {isSelectingScreen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          style={{ zIndex: 2000 }}
        >
          <div className="rounded-lg p-6 text-center" style={{ background: 'var(--theme-card, #1e1e1e)' }}>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--card-title-color, var(--theme-foreground, #f0f0f0))' }}>{t('selecting_screen')}</h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary, var(--theme-foreground-secondary, #aaa))' }}>{t('please_select_screen_to_record')}</p>
          </div>
        </div>
      )}
      
      {/* Preview and Crop UI */}
      {showAreaSelector && !showPreview && !isSelectingScreen && selectedScreenStream && (
        <PreviewAndCrop
          screenStream={selectedScreenStream}
          onSelectArea={handleAreaSelected}
          onCancel={handleAreaSelectionCancel}
          initialArea={recordingOptions.selectedArea}
        />
      )}

      {/* Preview Screen */}
      {showPreview && recordedBlob && (
        /* Overlay scrolls on mobile portrait so all content is reachable */
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          style={{ zIndex: 2000, overflowY: 'auto' }}
        >
          {/* Closing the save screen without saving: asked here, above the save screen. */}
          {confirmDiscard && (
            <div
              className="fixed inset-0 flex items-center justify-center p-4"
              style={{ zIndex: 10, background: 'rgba(0,0,0,0.45)' }}
              onClick={() => setConfirmDiscard(false)}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label={t('rec_controls.discard_question', 'Discard this recording?')}
                onClick={(e) => e.stopPropagation()}
                className="rounded-xl p-5 w-full"
                style={{ maxWidth: 400, background: 'var(--card-bg, #fff)', color: 'var(--text-primary, #111827)', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}
              >
                <h3 className="text-base font-semibold mb-1">{t('rec_controls.discard_question', 'Discard this recording?')}</h3>
                <p className="text-sm mb-5" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                  {t('rec_controls.discard_unsaved', "It hasn't been saved. If you close now, the recording is lost.")}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    autoFocus
                    onClick={() => setConfirmDiscard(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ border: '1px solid var(--border-color, #e5e7eb)', background: 'transparent', color: 'inherit' }}
                  >
                    {t('rec_controls.keep_editing', 'Keep editing')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setConfirmDiscard(false); onClose?.(); }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ border: 'none', background: '#dc2626', color: '#fff' }}
                  >
                    {t('rec_controls.discard', 'Discard')}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div
            className="flex min-h-full items-center justify-center px-4"
            style={{ paddingBlock: '1rem' }}
          >
            <div className="rounded-lg w-full max-w-3xl" style={{ background: 'var(--theme-card, #1e1e1e)', color: 'var(--card-foreground, var(--theme-foreground, #f0f0f0))' }}>
              {/* Sticky header so Close is always reachable */}
              <div
                className="flex items-center justify-between p-4"
                style={{ position: 'sticky', top: 0, background: 'var(--theme-card, #1e1e1e)', zIndex: 1, borderRadius: '8px 8px 0 0', borderBottom: '1px solid var(--border-color, #e0e0e0)' }}
              >
                <h2 className="text-lg font-bold" style={{ color: 'var(--card-title-color, var(--theme-foreground, #f0f0f0))' }}>{t('preview_save_title')}</h2>
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={isUploading}
                  aria-label={t('close', 'Close')}
                  className="rounded-md p-1.5 transition-colors"
                  style={{
                    color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--text-secondary, #525252)';
                    e.currentTarget.style.backgroundColor =
                      'var(--surface-hover, rgba(0, 0, 0, 0.08))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color =
                      'var(--text-primary, var(--theme-foreground, #1a1a1a))';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <X className="w-5 h-5" strokeWidth={2} aria-hidden />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Video capped at 45dvh so fields + buttons are visible without scrolling on portrait */}
                <div
                  className="relative bg-black rounded-lg overflow-hidden"
                  style={{ maxHeight: '45dvh' }}
                >
                  {videoUrl ? (
                    <video
                      ref={videoRef}
                      key={videoUrl}
                      autoPlay
                      playsInline
                      muted
                      controls
                      className="w-full rounded-lg"
                      style={{ maxHeight: '45dvh', objectFit: 'contain' }}
                    >
                      <source src={videoUrl} type="video/webm" />
                      {t('video_player.browser_unsupported')}
                    </video>
                  ) : (
                    <div className="w-full flex items-center justify-center py-12" style={{ color: 'var(--text-secondary, var(--theme-foreground-secondary, #888))' }}>
                      {t('no_video_available')}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label htmlFor="airec-save-title" className="block text-sm font-medium mb-2" style={{ color: 'var(--card-text-color, var(--text-secondary, #aaa))' }}>
                      {t('title_label')}
                    </label>
                    <input
                      id="airec-save-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      maxLength={TITLE_MAX_LENGTH}
                      className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      style={{
                        background: 'var(--bg-alternate, var(--theme-background, #2a2a2a))',
                        color: 'var(--text-primary, var(--theme-foreground, #f0f0f0))',
                        border: '1px solid var(--border-color, #444)',
                      }}
                      placeholder={t('title_placeholder')}
                      required
                    />
                  </div>

                  <div>
                    <div className="block text-sm font-medium mb-2" style={{ color: 'var(--card-text-color, var(--text-secondary, #aaa))' }}>
                      {t('description_label')}
                    </div>
                    {/* Formatted description (bold, lists, links); stored as HTML. */}
                    <RichTextField
                      value={description}
                      onChange={setDescription}
                      placeholder={t('description_placeholder')}
                      ariaLabel={t('description_label')}
                      maxChars={DESCRIPTION_MAX_CHARS}
                      disabled={isUploading}
                    />
                  </div>
                </div>

                {/* Action buttons — stack on mobile, row on md+ */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                      style={{
                        border: '1px solid var(--border-color, #444)',
                        color: 'var(--text-primary, var(--theme-foreground, #f0f0f0))',
                        background: 'var(--bg-alternate, var(--theme-background, #2a2a2a))',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover, rgba(255,255,255,0.08))'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-alternate, var(--theme-background, #2a2a2a))'; }}
                    >
                      <RotateCcw className="w-4 h-4" />
                      {t('rerecord_button')}
                    </button>
                    
                    <button
                      onClick={handleDownload}
                      className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                      style={{
                        border: '1px solid var(--border-color, #444)',
                        color: 'var(--text-primary, var(--theme-foreground, #f0f0f0))',
                        background: 'var(--bg-alternate, var(--theme-background, #2a2a2a))',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover, rgba(255,255,255,0.08))'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-alternate, var(--theme-background, #2a2a2a))'; }}
                    >
                      <Download className="w-4 h-4" />
                      {t('download_button')}
                    </button>
                  </div>

                  <button
                    onClick={handleSave}
                    disabled={!title.trim() || isUploading}
                    className="px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium text-sm"
                    style={{
                      backgroundColor: 'var(--button-primary-bg, #4f46e5)',
                      color: 'var(--button-primary-foreground, #ffffff)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!(!title.trim() || isUploading)) {
                        e.currentTarget.style.backgroundColor = 'var(--button-primary-hover-bg, #4338ca)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(!title.trim() || isUploading)) {
                        e.currentTarget.style.backgroundColor = 'var(--button-primary-bg, #4f46e5)';
                      }
                    }}
                  >
                    <Upload className="w-4 h-4" />
                    {isUploading ? t('uploading_button') : t('save_video_button')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main recorder UI - only show when area selector and preview are not active.
          Hidden during the countdown, which takes the whole screen. */}
      {!showAreaSelector && !showPreview && countdown === null && (
        <div
          className={isLive
            ? 'fixed inset-0 z-50'
            : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'}
          // While recording: no backdrop and click-through, so the page underneath
          // (including this tab) stays usable. The floating controls opt back in below.
          style={{ zIndex: 2000, pointerEvents: isLive ? 'none' : undefined }}
        >
          {/* Floating Controls */}
          {(isRecordingActive || isRecording) && (
            <div style={{ pointerEvents: 'auto' }}>
            <FloatingControls
              isRecording={isRecording}
              isPaused={isPaused}
              recordingTime={formatRecordingTime(parseInt(recordingTime.split(':')[0]) * 60 + parseInt(recordingTime.split(':')[1]))}
              onStop={handleStopRecording}
              onPause={handlePauseRecording}
              onResume={handleResumeRecording}
              onDelete={handleDeleteRecording}
              hasCamera={hasCamera}
              isCameraOn={isCameraOn}
              onToggleCamera={handleToggleCamera}
              onOpenCameraPreview={pipSupported && !previewOpen ? () => void openPreview() : undefined}
              inlinePreviewStream={!pipSupported && isCameraOn ? cameraStream : null}
              hasMic={hasMic}
              isMicOn={isMicOn}
              onToggleMic={toggleAudio}
            />
            </div>
          )}

          {/* Recording Setup Panel */}
          {showSetupPanel && !isRecording && (
            <RecordingSetupPanel
              onClose={() => onClose?.()}
              recordingOptions={recordingOptions}
              onOptionChange={(options) => {
                // Ensure we maintain all properties when updating options
                setRecordingOptions({
                  ...recordingOptions,
                  ...options
                });
              }}
              onStartRecording={handleStartRecording}
            />
          )}
        </div>
      )}

      {storageExceeded && (
        <StorageExceededDialog
          storageInfo={storageInfo}
          onClose={() => setStorageExceeded(false)}
        />
      )}
    </>
  );
};

// Wrapper component that handles the isOpen condition
const ScreenRecorder: React.FC<ScreenRecorderProps> = ({ isOpen, onClose, initialRecordingMode, onBlockingChange }) => {
  if (!isOpen) return null;
  return <ScreenRecorderContent onClose={onClose} initialRecordingMode={initialRecordingMode} onBlockingChange={onBlockingChange} />;
};

export default ScreenRecorder;
